import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import type { SchemaProblem, ValidateSchemaResult } from '../../lib/validateSchema';
import { ValidateSummary } from '../ValidateSummary/ValidateSummary';
import { ReportIcon } from '../ValidateSummary/ReportIcon';
import { summarizeReport } from '../ValidateSummary/reportSummary';

export interface ValidationStripProps {
  /** Null until the schema has been checked. */
  report: ValidateSchemaResult | null;
  anchoredProblems: SchemaProblem[];
  schemaProblems: SchemaProblem[];
  tab: 'fields' | 'rules';
  onJumpToProblem: (problem: SchemaProblem) => void;
  /** Absent while read-only. */
  onFix?: (problem: SchemaProblem) => void;
  onClear: () => void;
  /** Whether the details popover is open, so the caller can hold checks meanwhile. */
  onDetailsOpenChange?: (open: boolean) => void;
  /** A new report removed the control that held focus; the caller moves it somewhere real. */
  onFocusLost?: () => void;
}

/**
 * One line whose height does not depend on the report.
 *
 * It sits directly above the grid in a fixed-height card, so any change in its
 * height moves every visible row under the cursor. The findings therefore open
 * in a popover over the grid instead of stacking in the flow.
 *
 * The status region is always mounted and only its text changes: screen readers
 * announce changes to a region that already exists, and routinely miss one that
 * appears together with its text.
 */
export function ValidationStrip({
  report,
  anchoredProblems,
  schemaProblems,
  tab,
  onJumpToProblem,
  onFix,
  onClear,
  onDetailsOpenChange,
  onFocusLost,
}: ValidationStripProps) {
  const { t } = useTranslation('knowledge');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const clearRef = useRef<HTMLButtonElement>(null);

  const hasProblems = !!report && report.problems.length > 0;
  // Otherwise a popover left open over a report that went clean reopens by
  // itself with the next report that has findings.
  if (open && !hasProblems) setOpen(false);
  const detailsOpen = open && hasProblems;

  // Read before the commit removes the control holding focus: once it is gone,
  // focus has already fallen back to the page.
  const focusWillBeLostRef = useRef(false);
  const active = typeof document === 'undefined' ? null : document.activeElement;
  if (active && active !== document.body) {
    const detailsGoing =
      !hasProblems && (active === triggerRef.current || !!contentRef.current?.contains(active));
    const clearGoing = !report && active === clearRef.current;
    if (detailsGoing || clearGoing) focusWillBeLostRef.current = true;
  }
  useLayoutEffect(() => {
    if (!focusWillBeLostRef.current) return;
    focusWillBeLostRef.current = false;
    onFocusLost?.();
  });

  const onDetailsOpenChangeRef = useRef(onDetailsOpenChange);
  onDetailsOpenChangeRef.current = onDetailsOpenChange;
  useEffect(() => {
    onDetailsOpenChangeRef.current?.(detailsOpen);
  }, [detailsOpen]);
  useEffect(() => () => onDetailsOpenChangeRef.current?.(false), []);

  const tint = !report
    ? 'border-border-light bg-surface/40'
    : report.errorCount > 0
      ? 'border-destructive/30 bg-destructive/10'
      : report.warningCount > 0
        ? 'border-warning/30 bg-warning/5'
        : 'border-border-light bg-surface/40';

  return (
    <div
      data-testid="validation-strip"
      className={`flex h-9 shrink-0 items-center gap-xs overflow-hidden rounded-lg border px-sm text-xs transition-colors duration-fast ease-out ${tint}`}
    >
      {report && <ReportIcon report={report} />}
      <span
        role="status"
        aria-atomic="true"
        className={`min-w-0 flex-1 truncate whitespace-nowrap ${
          report ? 'font-medium text-foreground' : 'text-muted-foreground'
        }`}
      >
        {report ? summarizeReport(report, t) : t('taxonomy.taxonomyDetail.notCheckedYet')}
      </span>

      {hasProblems && (
        <Popover open={detailsOpen} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button ref={triggerRef} variant="ghost" size="mini">
              {t('taxonomy.taxonomyDetail.showDetails')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            ref={contentRef}
            align="end"
            aria-label={t('taxonomy.taxonomyDetail.validationDetails')}
            className="flex max-h-96 w-96 flex-col overflow-y-auto p-sm text-xs"
          >
            <ValidateSummary
              report={report}
              anchoredProblems={anchoredProblems}
              schemaProblems={schemaProblems}
              tab={tab}
              hideHeadline
              onJumpToProblem={(problem) => {
                setOpen(false);
                onJumpToProblem(problem);
              }}
              onFix={
                onFix &&
                ((problem) => {
                  setOpen(false);
                  onFix(problem);
                })
              }
            />
          </PopoverContent>
        </Popover>
      )}
      {report && (
        <Button ref={clearRef} variant="ghost" size="mini" onClick={onClear}>
          {t('taxonomy.taxonomyDetail.clear')}
        </Button>
      )}
    </div>
  );
}
